/**
 * Extracts MIME type and base64 data from a data URL
 * @param dataUrl The data URL string
 * @returns { mimeType: string, base64Data: string }
 */
const extractDataUrl = (
  dataUrl: string,
): { mimeType: string; base64Data: string } => {
  const matches = dataUrl.match(/^data:([\w.+-]+\/[\w.+-]+);base64,(.+)$/);
  if (!matches || matches.length !== 3) {
    throw new Error("Invalid data URL format");
  }
  return {
    mimeType: matches[1],
    base64Data: matches[2],
  };
};

/**
 * Converts a base64 string to a Blob object
 * @param base64 The base64 string to convert (can be a data URL or raw base64)
 * @param mimeType Optional MIME type. If not provided, it will be extracted from the data URL
 * @returns Blob
 */
export const base64ToBlob = (base64: string, mimeType?: string): Blob => {
  let finalBase64: string;
  let finalMimeType: string;

  if (base64.startsWith("data:")) {
    const { mimeType: extractedMimeType, base64Data } = extractDataUrl(base64);
    finalBase64 = base64Data;
    finalMimeType = mimeType || extractedMimeType;
  } else {
    finalBase64 = base64;
    if (!mimeType) {
      throw new Error("MIME type is required when base64 is not a data URL");
    }
    finalMimeType = mimeType;
  }

  // Convert base64 to binary string
  const binaryString = atob(finalBase64);

  // Convert binary string to Uint8Array
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Create and return the Blob
  return new Blob([bytes], { type: finalMimeType });
};

/**
 * Converts a base64 string to a File object
 * @param base64 The base64 string to convert (can be a data URL or raw base64)
 * @param fileName The name of the file
 * @param mimeType Optional MIME type. If not provided, it will be extracted from the data URL
 * @returns File
 */
export const base64ToFile = (
  base64: string,
  fileName: string,
  mimeType?: string,
): File => {
  const blob = base64ToBlob(base64, mimeType);
  return new File([blob], fileName, { type: blob.type });
};

/**
 * Synchronously reads a blob: URL into a File. Uses a synchronous
 * XMLHttpRequest with overrideMimeType so the binary bytes survive the
 * text decoding, then rebuilds them into a File of the given type.
 */
export const blobUrlToFile = (
  blobUrl: string,
  fileName: string,
  mimeType: string,
): File => {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", blobUrl, false);
  xhr.overrideMimeType("text/plain; charset=x-user-defined");
  xhr.send();
  const text = xhr.responseText;
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    bytes[i] = text.charCodeAt(i) & 0xff;
  }
  return new File([bytes], fileName, { type: mimeType });
};
